import { paymentMethods } from "../../containers/SeasonPass/constants";
import axios from "../../axios-instance.js";
import { OrderCreationModel, TransactionCreationRequest, TransactionSeatModel } from "./paymentModels.js";
import { getCurrencieLabel } from "../../constants/currencies.js";
import crypto from "crypto-js";
import { getTokenFormPaymentApi } from "../getTokenFormPaymentApi.js";
import { paymentTypes } from "../../constants/paymentType.ts";

import swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(swal);

export interface PaymentRequest {
    paymentMethod: number | null,
    paymentProvider: number | null,
    paymentType: number,
    couponCode: string,
    redirectUrl: string,
    eventData: any,
    coupon: any,
    ignoreCustomFields?: boolean,
    transaction: {
        id?: string,
        fullName: string,
        physicalAddress: string,
        city: string,
        postalCode: string,
        phoneNumber: string,
        buyerEmail: string,
        birthday: string,
        gender: string,
        companyName: string,
        companyCity?: string,
        companyCountry?: string,
        companyAddress: string,
        companyVatNumber: string,
        buyerCountry: string,
        transactionFieldValues: any[],
    },
    tickets: any[],
}

export class CreateTransactionResponse {
    transactionId: string;
    invoiceId: string;
    paymentUrl: string;
    redirectUrl: string;
    paymentProvider: number | null;

    constructor(transactionId: string, invoiceId: string, paymentUrl: string, redirectUrl: string, paymentProvider: number | null) {
        this.transactionId = transactionId;
        this.invoiceId = invoiceId;
        this.paymentUrl = paymentUrl;
        this.redirectUrl = redirectUrl;
        this.paymentProvider = paymentProvider;
    }
}

export const createTransaction = async (paymentInfo: PaymentRequest) => {
    let seats = paymentInfo.tickets.map(t => {
        return {
            ticketTemplateId: t.ticketTemplateId,
            area: t.area,
            side: t.side,
            row: t.row,
            seat: t.seat,
            optionalParameters: t.optionalParameters,
            discount: t.discount,
            fixedDiscount: t.fixedDiscount
        }
    });

    const request: TransactionCreationRequest = {
        paymentMethodId: paymentInfo.paymentMethod != null ? paymentInfo.paymentMethod : 0,
        paymentProvider: paymentInfo.paymentProvider,
        successUrl: paymentInfo.redirectUrl,
        ignoreCustomFields: !paymentInfo.ignoreCustomFields ? false : true,
        transaction: {
            id: paymentInfo.transaction.id,
            amount: 0,
            currency: getCurrencieLabel(paymentInfo.eventData.currencyId),
            fullName: paymentInfo.transaction.fullName,
            physicalAddress: paymentInfo.transaction.physicalAddress,
            city: paymentInfo.transaction.city,
            postalCode: paymentInfo.transaction.postalCode,
            phoneNumber: paymentInfo.transaction.phoneNumber,
            buyerEmail: paymentInfo.transaction.buyerEmail,
            birthday: paymentInfo.transaction.birthday,
            gender: paymentInfo.transaction.gender,

            companyEmail: '',
            companyName: paymentInfo.transaction.companyName,
            companyAddress: paymentInfo.transaction.companyAddress,
            companyCity: paymentInfo.transaction.companyCity,
            companyPostalCode: '',
            companyCountry: paymentInfo.transaction.companyCountry,
            buyerCountry: paymentInfo.transaction.buyerCountry,
            companyVatNumber: paymentInfo.transaction.companyVatNumber,

            paymentType: paymentInfo.paymentType,
            couponCode: paymentInfo.couponCode,
            seats: seats,
            tagNames: [],
            transactionFieldValues: paymentInfo.transaction.transactionFieldValues,
        }
    }

    const { data } = await axios.post("Transaction/createForSell", request);

    return new CreateTransactionResponse(
        data.transactionId,
        data.invoiceId,
        data.paymentUrl,
        data.redirectUrl,
        data.paymentProvider,
    );
}

export const executePayment = async (request: PaymentRequest) => {
    const { invoiceId, paymentProvider, paymentUrl, redirectUrl } = await createTransaction(request);

    switch (paymentProvider) {
        case paymentMethods.LedPay:
            await executeLedPay(request, invoiceId);
            MySwal.close();
            break;

        case paymentMethods.PaySpot:
            await executePaySpot(request, invoiceId);
            MySwal.close();
            break;

        case paymentMethods.PayNl:
            MySwal.close();
            await executePayNl(request, paymentUrl);
            break;

        case paymentMethods.OffPay:
            MySwal.close();
            await executeOffPay(request, redirectUrl);
            break;

        default:
            break;
    }
}

export const executeOrderPayment = async (request: OrderCreationModel) => {
    const { data } = await axios.post('/Order', request);

    switch (data.paymentProvider) {
        case paymentMethods.OffPay:
            window.location.href = data.returnUrl;
            return;
        case paymentMethods.LedPay:
            await executeOrderLedPay(data);
            break;

        case paymentMethods.PaySpot:
            await executeOrderPaySpot(data);
            break;

        case paymentMethods.PayNl:
            await executeOrderPayNl(request);
            break;

        default:
            break;
    }
}

async function executeLedPay(request: PaymentRequest, invoiceId: string) {
    if (request.paymentType !== paymentTypes.delayedPayment) {
        const formData = await getLedPayOrderData(invoiceId);

        const form = document.createElement('form');
        form.setAttribute('method', 'post');
        form.setAttribute('target', '_self');

        for (const [key, value] of Object.entries<any>(formData)) {
            if (key === 'action') {
                continue;
            }
            const input = document.createElement('input');
            input.setAttribute('name', key);
            input.setAttribute('value', value);
            input.setAttribute('type', 'hidden');
            form.append(input);
        }
        form.setAttribute('action', formData.action);
        // form.setAttribute('action', process.env.REACT_APP_PAYMENT_LINK || '');

        document.getElementsByTagName('body')[0].append(form);

        form.submit();
    }
}

async function executeOffPay(request: PaymentRequest, redirectUrl: string) {
    window.location.href = redirectUrl;
}

async function executeOrderLedPay(data: any) {
    const formData = await getLedPayOrderData(data.orderId);

    const form = document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('target', '_self');

    for (const [key, value] of Object.entries<any>(formData)) {
        const input = document.createElement('input');
        input.setAttribute('name', key);
        input.setAttribute('value', value);
        input.setAttribute('type', 'hidden');
        form.append(input);
    }

    form.setAttribute('action', process.env.REACT_APP_PAYMENT_LINK || '');

    document.getElementsByTagName('body')[0].append(form);

    form.submit();
}

async function executeOrderPayNl(data: any) {
    window.open(data.paymentUrl, '_self');
}

async function executeOrderPaySpot(data: any) {
    const formData = await getPayspotInvoiceData(data.orderId);

    const form = document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('target', '_self');
    form.setAttribute('action', formData.action);

    for (const [key, value] of Object.entries<any>(formData)) {

        if (key === 'action'
            || key === 'invoiceId'
        ) {
            continue;
        }

        const input = document.createElement('input');
        input.setAttribute('name', key.toUpperCase());
        input.setAttribute('value', value);
        form.append(input);
    }


    document.getElementsByTagName('body')[0].append(form);

    form.submit();
}

async function executePaySpot(request: PaymentRequest, invoiceId: string) {
    const formData = await getPayspotInvoiceData(invoiceId);

    const form = document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('target', '_self');
    form.setAttribute('action', formData.action);

    for (const [key, value] of Object.entries<any>(formData)) {

        if (key === 'action'
            || key === 'invoiceId'
        ) {
            continue;
        }

        const input = document.createElement('input');
        input.setAttribute('name', key);
        input.setAttribute('value', value);
        form.append(input);
    }


    document.getElementsByTagName('body')[0].append(form);

    form.submit();
}

async function executePayNl(request: PaymentRequest, paymentUrl: string) {
    window.open(paymentUrl, '_self');
}

async function getPayspotInvoiceData(orderId: string) {
    const { data } = await axios.get(`Payment/paymentInfo/${orderId}`);

    return data.paySpot;
}

async function getLedPayOrderData(orderId: string) {
    const { data } = await axios.get(`Payment/paymentInfo/${orderId}`);

    const token = await getTokenFormPaymentApi({ merchantId: data.merchantId });

    const ledRequest = {
        ...data.ledPay,
        accessToken: token.data.access_token,
        merchantSig: '',
    }

    ledRequest.merchantSig = createMerchantSin(ledRequest, ledRequest.signedKeys);

    return ledRequest;
}

export const createMerchantSin = (pairs, pairsKeys = "") => {
    let keys = pairsKeys.split(",");
    keys = keys.sort();

    const signData = keys;

    keys.forEach((key) => {
        const val = pairs[key].toString();
        signData.push(val.replace(/\\/g, "\\\\").replace(/:/g, "\\:"));
    });

    return crypto.enc.Base64.stringify(
        crypto.HmacSHA256(signData.join(":"), crypto.enc.Hex.parse(process.env.REACT_APP_HMAC_KEY || ''))
    );
};

export const getActivatedRules = (tickets: any[], ticketTemplates: any[]): any[] => {
    const activatedRules = [];

    for (const ticketTemplate of ticketTemplates) {
        if (ticketTemplate.ticketRules.length === 0) {
            continue;
        }

        const ticks = tickets.filter(t => t.ticketTemplateId === ticketTemplate.ticketTemplateId);

        let rule = ticketTemplate.ticketRules.filter(r => r.count <= ticks.length).sort(r => -r.count);
        if (rule.length > 0) {
            rule = rule[0]
            activatedRules.push(rule);
        }

    }

    return activatedRules;
};